import {
  array,
  assert,
  boolean,
  define,
  literal,
  number,
  object,
  optional,
  string,
  union,
} from 'superstruct';

import { Api } from '../apis/api';
import { IImportLeadPayload } from '../types/api';

import { getVisibleElementById } from './dom';

const emailRe = /^.+@.+\..+$/i;

export const validateEmail = (value: unknown) => {
  if (typeof value === 'string' && value.length <= 320) {
    if (!emailRe.test(value)) return false;
    const [local, domain] = value.split('@');
    return local.length <= 64 && domain.length <= 255;
  }
  return false;
};

const emailStr = () => define<string>('email', validateEmail);

export const isValidUrl = (url?: string) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

export const validateMvpLead = () => {
  const email = getVisibleElementById<'input'>('emailAddress')?.value as string;
  const firstName = getVisibleElementById<'input'>('firstName')
    ?.value as string;
  const lastName = getVisibleElementById<'input'>('lastName')?.value as string;

  if (email && firstName && lastName) {
    return validateEmail(email) && firstName.length > 0 && lastName.length > 0;
  }
  return false;
};

export const defaultCardParameters = {
  niceType: 'Mastercard',
  type: 'mastercard',
  patterns: [[51, 55], [2221, 2229], [223, 229], [23, 26], [270, 271], 2720],
  gaps: [4, 8, 12],
  lengths: [16],
  code: {
    name: 'CVC',
    size: 3,
  },
  matchStrength: 2,
};

const LeadSchema = object({
  campaignId: number(),
  sessionId: optional(string()),
  client: string(),
  orderId: optional(string()),
  paySource: optional(string()),

  customerId: optional(number()),
  firstName: string(),
  lastName: string(),
  phoneNumber: optional(string()),
  emailAddress: emailStr(),
  ipAddress: string(),

  country: string(),
  city: string(),
  state: string(),
  address1: string(),
  postalCode: string(),
  shipPostalCode: optional(string()),
  billShipSame: boolean(),

  affId: optional(string()),
  custom_fbc: optional(string()),
  custom_fbp: optional(string()),
  custom_ttp: optional(string()),
  custom_fbclid: optional(string()),
  custom_order_vlmid: optional(string()),
  custom_ttclid: optional(string()),
  custom_user_agent: optional(string()),
  custom_receive_sms: optional(string()),
  custom_sms_consent: optional(string()),
  custom_http_referer: optional(string()),
  custom_order_auid: optional(string()),
  custom_order_language: optional(string()),
  custom_order_10_off_vip: optional(boolean()),
  custom_gl: optional(union([string(), array(string())])),
  custom_gclid: optional(union([string(), array(string())])),
  custom_wbraid: optional(union([string(), array(string())])),
  custom_gbraid: optional(union([string(), array(string())])),
  custom_taboola_clid: optional(union([string(), array(string())])),
  custom_ob_click_id: optional(union([string(), array(string())])),
  custom_order_marketing_opt_in: window.IS_MAIN
    ? literal(true)
    : optional(boolean()),
  custom_order_affId: optional(string()),
  custom_order_ttp: optional(string()),
  custom_order_fbc: optional(string()),
  custom_order_fbp: optional(string()),
  custom_order_fbclid: optional(string()),
  custom_order_ga: optional(string()),
  custom_order_ad_id: optional(string()),
  custom_order_gclid: optional(union([string(), array(string())])),
  custom_order_ttclid: optional(string()),
  custom_order_wbraid: optional(union([string(), array(string())])),
  custom_order_gbraid: optional(union([string(), array(string())])),
  custom_order_ob_click_id: optional(union([string(), array(string())])),
  custom_order_gl: optional(union([string(), array(string())])),
  custom_order_taboola_clid: optional(union([string(), array(string())])),
  custom_order_http_referer: optional(string()),
  custom_order_device_browser: optional(string()),
  custom_order_device_browser_version: optional(string()),
  custom_order_device_os: optional(string()),
  custom_order_device_os_version: optional(string()),
  custom_order_device_type: optional(string()),
  custom_order_device_user_agent: optional(string()),
  custom_vcid: optional(string()),
  custom_order_vcid: optional(string()),
  custom_order_m_ab_var: optional(string()),
  custom_order_m_ab_name: optional(string()),
  custom_order_ab_name: optional(string()),
  custom_order_ab_var: optional(string()),
  custom_order_campaign_id: optional(string()),
  custom_order_platform: optional(string()),
  custom_order_publisher_name: optional(string()),
  custom_order_site_id: optional(string()),
  custom_order_creative_id: optional(string()),
  custom_order_creative_name: optional(string()),
  custom_order_flight_id: optional(string()),
  custom_order_flight_name: optional(string()),
  custom_order_mg_tracking_id: optional(string()),
  custom_order_nb_tracking_id: optional(string()),
  custom_order_newsbreak_cid: optional(string()),
  custom_order_tb_click_id: optional(string()),
  custom_order_f_ver: optional(string()),
  custom_order_ScCid: optional(string()),
  custom_order_rb_click_id: optional(string()),
  custom_order_controller_version: optional(string()),
  custom_order_force: optional(string()),
});

export const validateLead = (lead: IImportLeadPayload) => {
  assert(lead, LeadSchema);
};

export const validateZipCode = async (
  zip: string,
  state: string,
  country: string,
) => {
  if (country !== 'US') {
    return zip;
  }

  try {
    const { state: zipData } = await Api.validateZipCode(zip);
    if (zipData?.state && state === zipData.state) {
      return zip;
    } else {
      return exampleZipCodes[state];
    }
  } catch {
    return exampleZipCodes[state];
  }
};

const exampleZipCodes: Record<string, string> = {
  AL: '35004',
  AK: '99501',
  AZ: '85001',
  AR: '71601',
  CA: '90001',
  CO: '80001',
  CT: '06001',
  DE: '19701',
  FL: '32003',
  GA: '30002',
  HI: '96701',
  ID: '83201',
  IL: '60001',
  IN: '46001',
  IA: '50001',
  KS: '66002',
  KY: '40003',
  LA: '70001',
  ME: '03901',
  MD: '20601',
  MA: '01001',
  MI: '48001',
  MN: '55001',
  MS: '38601',
  MO: '63005',
  MT: '59001',
  NE: '68001',
  NV: '88901',
  NH: '03031',
  NJ: '07001',
  NM: '87001',
  NY: '10001',
  NC: '27006',
  ND: '58001',
  OH: '43001',
  OK: '73001',
  OR: '97001',
  PA: '15001',
  RI: '02801',
  SC: '29001',
  SD: '57001',
  TN: '37010',
  TX: '75001',
  UT: '84001',
  VT: '05001',
  VA: '20105',
  WA: '98001',
  WV: '24701',
  WI: '53001',
  WY: '82001',
  AS: '96799',
  GU: '96910',
  MP: '96950',
  PR: '00601',
  VI: '00801',
};
